import { useTranslation } from "react-i18next";
import { useLangFont } from "../../../hooks/useLangFont";

const stepKeys = [
  { num: "01", icon: "ri-message-3-line", titleKey: "process_step_1_title", descKey: "process_step_1_desc", badgeKey: "" },
  { num: "02", icon: "ri-time-line", titleKey: "process_step_2_title", descKey: "process_step_2_desc", badgeKey: "process_step_2_badge" },
  { num: "03", icon: "ri-truck-line", titleKey: "process_step_3_title", descKey: "process_step_3_desc", badgeKey: "" },
  { num: "04", icon: "ri-tools-line", titleKey: "process_step_4_title", descKey: "process_step_4_desc", badgeKey: "process_step_4_badge" },
  { num: "05", icon: "ri-gift-line", titleKey: "process_step_5_title", descKey: "process_step_5_desc", badgeKey: "" },
];

export default function RepairProcess() {
  const { t } = useTranslation();
  const { heading, body } = useLangFont();

  const steps = stepKeys.map((s) => ({
    number: s.num,
    icon: s.icon,
    title: t(s.titleKey),
    desc: t(s.descKey),
    badge: s.badgeKey ? t(s.badgeKey) : null,
  }));

  return (
    <section id="process" className="py-28 bg-white w-full">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <span
            className="inline-block bg-[#C9A84C] text-[#1A2332] text-xs font-bold tracking-[0.3em] uppercase px-4 py-2 rounded-full mb-4"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            {t("process_label")}
          </span>
          <h2
            className="text-4xl md:text-5xl font-bold text-[#1A2332] mb-4 tracking-tight"
            style={{ fontFamily: heading }}
          >
            {t("process_title")}
          </h2>
          <p
            className="text-gray-500 text-sm tracking-wide"
            style={{ fontFamily: body }}
          >
            {t("process_subtitle")}
          </p>
        </div>

        {/* Desktop: horizontal stepper */}
        <div className="hidden md:flex items-start gap-0 relative">
          {steps.map((step, i) => (
            <div key={step.number} className="flex-1 flex flex-col items-center relative">
              {i < steps.length - 1 && (
                <div className="absolute top-[38px] left-1/2 w-full h-px bg-gradient-to-r from-[#C9A84C]/60 to-[#C9A84C]/20 z-0" />
              )}
              <div className="relative z-10 flex flex-col items-center">
                <div className="w-[76px] h-[76px] flex items-center justify-center rounded-full bg-[#1A2332] border-2 border-[#C9A84C]/50 mb-4">
                  <i className={`${step.icon} text-[#C9A84C] text-2xl`} />
                </div>
                {step.badge && (
                  <span
                    className="absolute -top-2 -right-2 bg-[#C9A84C] text-[#1A2332] text-[9px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {step.badge}
                  </span>
                )}
              </div>
              <div className="text-center px-3">
                <p
                  className="text-[#C9A84C] text-[10px] font-bold tracking-[0.25em] mb-1"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  STEP {step.number}
                </p>
                <h3
                  className="text-[#1A2332] font-bold text-sm mb-2 tracking-tight"
                  style={{ fontFamily: heading }}
                >
                  {step.title}
                </h3>
                <p
                  className="text-gray-500 text-xs leading-relaxed"
                  style={{ fontFamily: body }}
                >
                  {step.desc}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Mobile: vertical list */}
        <div className="flex md:hidden flex-col gap-6">
          {steps.map((step, i) => (
            <div key={step.number} className="flex gap-4 items-start">
              <div className="flex flex-col items-center">
                <div className="relative w-14 h-14 flex items-center justify-center rounded-full bg-[#1A2332] border-2 border-[#C9A84C]/50 shrink-0">
                  <i className={`${step.icon} text-[#C9A84C] text-xl`} />
                  {step.badge && (
                    <span
                      className="absolute -top-2 -right-3 bg-[#C9A84C] text-[#1A2332] text-[8px] font-bold px-1.5 py-0.5 rounded-full whitespace-nowrap"
                      style={{ fontFamily: "'Montserrat', sans-serif" }}
                    >
                      {step.badge}
                    </span>
                  )}
                </div>
                {i < steps.length - 1 && <div className="w-px h-8 bg-[#C9A84C]/30 mt-2" />}
              </div>
              <div className="pt-1">
                <p
                  className="text-[#C9A84C] text-[9px] font-bold tracking-[0.25em] mb-0.5"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  STEP {step.number}
                </p>
                <h3
                  className="text-[#1A2332] font-bold text-sm mb-1"
                  style={{ fontFamily: heading }}
                >
                  {step.title}
                </h3>
                <p
                  className="text-gray-500 text-xs leading-relaxed"
                  style={{ fontFamily: body }}
                >
                  {step.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
